import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Share2, CheckCircle, AlertCircle, Info } from 'lucide-react';

const DISSEMINATION_METHODS = [
  { id: 'journal', label: 'Academic journal publication' },
  { id: 'conference', label: 'Conference presentation' },
  { id: 'thesis', label: 'Thesis or dissertation' },
  { id: 'report', label: 'Research report' },
  { id: 'book', label: 'Book or book chapter' },
  { id: 'policy', label: 'Policy briefing' },
  { id: 'media', label: 'Media / Press release' },
  { id: 'social_media', label: 'Social media' },
  { id: 'workshop', label: 'Workshop / Training' },
  { id: 'website', label: 'Website or blog' },
  { id: 'repository', label: 'Data repository (e.g., Open Science Framework (OSF))' },
  { id: 'participants', label: 'Feedback to participants' },
  { id: 'stakeholders', label: 'Stakeholder briefing' },
  { id: 'other', label: 'Other' }
];

function D10_Dissemination() {
  const { dataE, updateDataRaw, nextStep, previousStep, completeWizard } = useWizard();
  const dissemination = dataE.dissemination;
  const declarations = dataE.declarations;

  const updateDissemination = (field, value) => {
    updateDataRaw({
      'wizardE.dissemination': { ...dissemination, [field]: value }
    });
  };

  const updateDeclarations = (field, value) => {
    updateDataRaw({
      'wizardE.declarations': { ...declarations, [field]: value }
    });
  };

  const toggleMethod = (methodId) => {
    const currentMethods = dissemination.methods || [];
    const newMethods = currentMethods.includes(methodId)
      ? currentMethods.filter(m => m !== methodId)
      : [...currentMethods, methodId];
    updateDissemination('methods', newMethods);
  };

  const allDeclarationsComplete = declarations.confirmedAccuracy && 
                                   declarations.confirmedGDPR && 
                                   declarations.confirmedTraining;

  const handleComplete = () => {
    completeWizard('E');
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <Share2 className="step-icon" size={32} />
        <div>
          <h2>Step 10: Dissemination & Declarations</h2>
          <p className="step-description">
            Describe how you will share your findings and make the required declarations.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Dissemination Plans</h3>

        <div className="form-group">
          <label>How do you plan to disseminate your findings?</label>
          <div className="checkbox-grid">
            {DISSEMINATION_METHODS.map(method => (
              <label key={method.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={dissemination.methods?.includes(method.id) || false}
                  onChange={() => toggleMethod(method.id)}
                />
                <span>{method.label}</span>
              </label>
            ))}
          </div>
        </div>

        {dissemination.methods?.includes('other') && (
          <div className="form-group">
            <label>Please specify other dissemination method:</label>
            <input
              type="text"
              value={dissemination.otherMethod}
              onChange={(e) => updateDissemination('otherMethod', e.target.value)}
              placeholder="Describe the other dissemination method"
            />
          </div>
        )}

        <div className="form-group">
          <label>Data Sharing Plans</label>
          <textarea
            value={dissemination.dataSharing}
            onChange={(e) => updateDissemination('dataSharing', e.target.value)}
            placeholder="Will anonymized data be made available for secondary research? If so, where will it be deposited and under what conditions?"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>Embargo or Confidentiality Requirements</label>
          <textarea
            value={dissemination.embargoPlans}
            onChange={(e) => updateDissemination('embargoPlans', e.target.value)}
            placeholder="Are there any embargo periods or confidentiality requirements that affect publication? (e.g., funder requirements, commercial sensitivity)"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Participant Feedback</label>
          <textarea
            value={dissemination.participantFeedback}
            onChange={(e) => updateDissemination('participantFeedback', e.target.value)}
            placeholder="How will you share findings with participants? (e.g., summary report, presentation)"
            rows={3}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Declarations</h3>

        <div className="info-box info">
          <Info size={20} />
          <p>
            Please confirm the following declarations. All declarations must be confirmed 
            before submitting your ethics application.
          </p>
        </div>

        <div className="declaration-checklist">
          <label className={`declaration-item ${declarations.confirmedAccuracy ? 'confirmed' : ''}`}>
            <input
              type="checkbox"
              checked={declarations.confirmedAccuracy || false}
              onChange={(e) => updateDeclarations('confirmedAccuracy', e.target.checked)}
            />
            <div className="declaration-content">
              {declarations.confirmedAccuracy ? (
                <CheckCircle size={20} className="declaration-icon success" />
              ) : (
                <AlertCircle size={20} className="declaration-icon" />
              )}
              <span>
                I confirm that the information provided in this application is accurate and complete 
                to the best of my knowledge.
              </span>
            </div>
          </label>

          <label className={`declaration-item ${declarations.confirmedGDPR ? 'confirmed' : ''}`}>
            <input
              type="checkbox"
              checked={declarations.confirmedGDPR || false}
              onChange={(e) => updateDeclarations('confirmedGDPR', e.target.checked)}
            />
            <div className="declaration-content">
              {declarations.confirmedGDPR ? (
                <CheckCircle size={20} className="declaration-icon success" />
              ) : (
                <AlertCircle size={20} className="declaration-icon" />
              )}
              <span>
                I confirm that this research will comply with data protection legislation (including GDPR where applicable) 
                and institutional data protection policies.
              </span>
            </div>
          </label>

          <label className={`declaration-item ${declarations.confirmedTraining ? 'confirmed' : ''}`}>
            <input
              type="checkbox"
              checked={declarations.confirmedTraining || false}
              onChange={(e) => updateDeclarations('confirmedTraining', e.target.checked)}
            />
            <div className="declaration-content">
              {declarations.confirmedTraining ? (
                <CheckCircle size={20} className="declaration-icon success" />
              ) : (
                <AlertCircle size={20} className="declaration-icon" />
              )}
              <span>
                I confirm that all researchers involved have completed appropriate research ethics 
                and data protection training.
              </span>
            </div>
          </label>
        </div>

        <div className="form-group">
          <label>Additional Comments</label>
          <textarea
            value={declarations.additionalComments}
            onChange={(e) => updateDeclarations('additionalComments', e.target.value)}
            placeholder="Any additional information you wish to provide to the ethics committee"
            rows={4}
          />
        </div>
      </div>

      {!allDeclarationsComplete && (
        <div className="info-box warning">
          <AlertCircle size={20} />
          <p>
            Please confirm all declarations above before completing the ethics wizard.
          </p>
        </div>
      )}

      <NavigationButtons
        onPrevious={previousStep}
        onNext={allDeclarationsComplete ? handleComplete : null}
        disableNext={!allDeclarationsComplete}
        nextLabel="Complete & Generate Report"
      />
    </div>
  );
}

export default D10_Dissemination;
